import { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Typography, useMediaQuery } from '@mui/material';
import ShowroomTileCarousel from './ShowroomTileCarousel';

const scrimLayers = (uniform, radial, bottom, top) =>
  [
    `linear-gradient(rgba(0, 0, 0, ${uniform}), rgba(0, 0, 0, ${uniform}))`,
    `radial-gradient(ellipse 72% 58% at 50% 46%, rgba(0, 0, 0, ${radial}) 0%, rgba(0, 0, 0, ${radial * 0.43}) 58%, transparent 100%)`,
    `linear-gradient(to bottom, rgba(0, 0, 0, ${bottom}) 0%, transparent 48%)`,
    `linear-gradient(to top, rgba(0, 0, 0, ${top}) 0%, transparent 42%)`,
  ].join(', ');

/** Default — city / exterior photos (lighter than before; hover scrim unchanged). */
const SHOWROOM_TILE_SCRIM_DEFAULT = scrimLayers(0.36, 0.32, 0.26, 0.24);

/** Hover — lighter so interiors show through; center stays darker for labels. */
const SHOWROOM_TILE_SCRIM_HOVER = scrimLayers(0.3, 0.26, 0.22, 0.2);

const scrimTransition = 'opacity 0.55s ease';

const tileTextShadow = ['0 1px 4px rgba(0, 0, 0, 0.65)', '0 2px 12px rgba(0, 0, 0, 0.5)'].join(
  ', ',
);

const mapsCtaSx = {
  borderRadius: 0,
  letterSpacing: '0.2em',
  fontSize: 11,
  minWidth: 200,
  mt: 2.5,
  py: 1.5,
  color: 'common.white',
  borderColor: 'rgba(255,255,255,0.65)',
  opacity: 0,
  transform: 'translateY(8px)',
  transition:
    'opacity 0.3s ease, transform 0.3s ease, border-color 0.2s ease, background-color 0.2s ease',
  pointerEvents: 'none',
  '&:hover': {
    borderColor: 'common.white',
    bgcolor: 'rgba(255,255,255,0.08)',
  },
};

export default function ShowroomTile({ showroom, isActive = false, onActivate, onDeactivate }) {
  const [localHovered, setLocalHovered] = useState(false);
  const canHover = useMediaQuery('(hover: hover) and (pointer: fine)');
  const mapHref = showroom.mapHref ?? null;
  const hovered = canHover ? localHovered : isActive;
  const isTouchTile = Boolean(mapHref && !canHover);
  const cityLabel = showroom.city.charAt(0) + showroom.city.slice(1).toLowerCase();

  const setActive = useCallback(
    active => {
      if (canHover) {
        setLocalHovered(active);
      } else if (active) {
        onActivate?.();
      } else {
        onDeactivate?.();
      }
    },
    [canHover, onActivate, onDeactivate],
  );

  const handleTouchTileClick = useCallback(() => {
    if (!isTouchTile) return;
    if (isActive) {
      onDeactivate?.();
    } else {
      onActivate?.();
    }
  }, [isTouchTile, isActive, onActivate, onDeactivate]);

  const handleTouchKeyDown = useCallback(
    e => {
      if (!isTouchTile) return;
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleTouchTileClick();
      }
    },
    [isTouchTile, handleTouchTileClick],
  );

  const handleMapsCtaClick = useCallback(e => {
    e.stopPropagation();
  }, []);

  const rootIsLink = Boolean(mapHref && canHover);
  const rootComponent = rootIsLink ? 'a' : 'article';
  let rootAriaLabel;
  if (rootIsLink) {
    rootAriaLabel = `Open ${cityLabel} showroom location in Google Maps`;
  } else if (isTouchTile) {
    rootAriaLabel = `View ${cityLabel} showroom interiors`;
  }
  const tabIndex = isTouchTile || !rootIsLink ? 0 : undefined;

  return (
    <Box
      component={rootComponent}
      href={rootIsLink ? mapHref : undefined}
      target={rootIsLink ? '_blank' : undefined}
      rel={rootIsLink ? 'noopener noreferrer' : undefined}
      role={isTouchTile ? 'button' : undefined}
      tabIndex={tabIndex}
      aria-label={rootAriaLabel}
      aria-expanded={isTouchTile ? isActive : undefined}
      className={`showroom-tile${hovered ? ' showroom-tile--active' : ''}`}
      onClick={isTouchTile ? handleTouchTileClick : undefined}
      onKeyDown={isTouchTile ? handleTouchKeyDown : undefined}
      onMouseEnter={canHover ? () => setActive(true) : undefined}
      onMouseLeave={canHover ? () => setActive(false) : undefined}
      onFocus={canHover ? () => setActive(true) : undefined}
      onBlur={
        canHover
          ? e => {
              if (!e.currentTarget.contains(e.relatedTarget)) setActive(false);
            }
          : undefined
      }
      sx={{
        position: 'relative',
        flex: { xs: '1 1 100%', md: '1 1 50%' },
        minHeight: { xs: 420, sm: 480, md: 560, lg: 640 },
        overflow: 'hidden',
        outline: 'none',
        cursor: mapHref ? 'pointer' : 'default',
        textDecoration: 'none',
        color: 'inherit',
      }}
    >
      <Box className="showroom-tile-media" sx={{ position: 'absolute', inset: 0 }} aria-hidden>
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          className="showroom-tile-exterior"
          src={showroom.exteriorImage}
          alt=""
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            opacity: hovered ? 0 : 1,
            transition: 'opacity 0.55s ease',
          }}
        />
        {hovered ? <ShowroomTileCarousel slides={showroom.interiorSlides} /> : null}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 1,
            pointerEvents: 'none',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              background: SHOWROOM_TILE_SCRIM_DEFAULT,
              opacity: hovered ? 0 : 1,
              transition: scrimTransition,
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              background: SHOWROOM_TILE_SCRIM_HOVER,
              opacity: hovered ? 1 : 0,
              transition: scrimTransition,
            }}
          />
        </Box>
      </Box>

      <Box
        className="showroom-tile-frame"
        sx={{
          position: 'absolute',
          inset: { xs: 12, md: 16 },
          border: '1px solid',
          borderColor: 'common.white',
          opacity: hovered ? 1 : 0,
          transition: 'opacity 0.4s ease',
          pointerEvents: 'none',
          zIndex: 2,
        }}
      />

      <Box
        sx={{
          position: 'relative',
          zIndex: 3,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2,
          py: 4,
          textAlign: 'center',
          color: 'common.white',
          textTransform: 'uppercase',
          textShadow: tileTextShadow,
        }}
      >
        <Typography
          component="h3"
          sx={{
            fontWeight: 600,
            letterSpacing: '0.12em',
            fontSize: { xs: '1.75rem', md: '2.25rem', lg: '2.5rem' },
            lineHeight: 1.15,
            mb: 1.5,
          }}
        >
          {showroom.city}
        </Typography>
        <Typography
          component="p"
          sx={{
            fontWeight: 500,
            letterSpacing: '0.18em',
            fontSize: { xs: 10, md: 11 },
            lineHeight: 1.6,
            maxWidth: 320,
          }}
        >
          {showroom.name}
        </Typography>
        {showroom.region ? (
          <Typography
            component="p"
            sx={{
              fontWeight: 400,
              letterSpacing: '0.14em',
              fontSize: { xs: 9, md: 10 },
              lineHeight: 1.6,
              mt: 0.75,
              opacity: 0.95,
              maxWidth: 320,
            }}
          >
            {showroom.region}
          </Typography>
        ) : null}
        {isTouchTile ? (
          <Button
            component="a"
            href={mapHref}
            target="_blank"
            rel="noopener noreferrer"
            variant="outlined"
            tabIndex={hovered ? 0 : -1}
            aria-hidden={!hovered}
            aria-label={`Open ${cityLabel} in Google Maps`}
            onClick={handleMapsCtaClick}
            sx={{
              ...mapsCtaSx,
              ...(hovered
                ? {
                    opacity: 1,
                    transform: 'translateY(0)',
                    pointerEvents: 'auto',
                  }
                : {}),
            }}
          >
            Open in Maps
          </Button>
        ) : null}
      </Box>
    </Box>
  );
}

ShowroomTile.propTypes = {
  showroom: PropTypes.shape({
    id: PropTypes.string.isRequired,
    city: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    region: PropTypes.string,
    videoHref: PropTypes.string,
    mapHref: PropTypes.string,
    exteriorImage: PropTypes.string.isRequired,
    interiorSlides: PropTypes.array.isRequired,
  }).isRequired,
  isActive: PropTypes.bool,
  onActivate: PropTypes.func,
  onDeactivate: PropTypes.func,
};
